/**
 * Roster / Participation checklist (FR-EVENTS-05, AC-PART-001).
 *
 * Roster-driven: the GET returns every live player, recorded or not, so this is
 * a complete checklist rather than an empty list. Edits batch into one `PUT` —
 * a 60-player checklist is one coaching action and one audit row.
 *
 * Write gating needs all three of: persona (`PARTICIPATION_WRITE_PERSONAS` —
 * stat operators read only), the server's own `can_edit` verdict, and an event
 * that is not closed (a closed event is frozen to Team Admin for film review).
 *
 * Participation is non-PII program data (T2, non-FERPA): who was rostered for
 * the event, nothing more. No medical, contact or academic fields belong here.
 */
import { useEffect, useMemo, useState } from 'react';

import { useApiClient } from '../../app/providers';
import { useToast } from '../../app/toast-context';
import { DeskSection } from '../../components/desk-section';
import { EmptyState } from '../../components/empty-state';
import { ApiError } from '../../lib/api-error';
import type { TeamEvent } from '../../lib/api-types';
import { PARTICIPATION_NOTES_MAX_LENGTH } from '../../lib/constants';
import type { Persona } from '../../lib/personas';

import {
  describePlayer,
  isParticipationChanged,
  toParticipationDraft,
  type DraftEntry,
  type DraftMap,
} from './participation-draft';
import {
  canPersona,
  EVENT_REOPEN_PERSONAS,
  PARTICIPATION_WRITE_PERSONAS,
  useParticipation,
} from './use-events';

export function ParticipationSection({
  event,
  persona,
}: {
  event: TeamEvent;
  persona: Persona | null;
}): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const participation = useParticipation(event.event_id);
  const [draft, setDraft] = useState<DraftMap>({});
  const [search, setSearch] = useState('');
  const [isUnrecordedOnly, setIsUnrecordedOnly] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const entries = participation.page?.participation ?? [];

  useEffect(() => setDraft(toParticipationDraft(entries)), [participation.page]);

  const isPersonaAllowed = canPersona(persona, PARTICIPATION_WRITE_PERSONAS);
  const isClosedLock = event.status === 'closed' && !canPersona(persona, EVENT_REOPEN_PERSONAS);
  const canWrite = isPersonaAllowed && !isClosedLock && participation.page?.can_edit !== false;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return entries.filter((entry) => {
      if (isUnrecordedOnly && entry.is_recorded) return false;
      if (needle === '') return true;
      return (
        entry.full_name.toLowerCase().includes(needle) ||
        (entry.jersey_number ?? '').toLowerCase().includes(needle) ||
        (entry.position ?? '').toLowerCase().includes(needle) ||
        entry.cohort.toLowerCase().includes(needle)
      );
    });
  }, [entries, isUnrecordedOnly, search]);

  const changed = entries.filter((entry) => isParticipationChanged(entry, draft[entry.player_id]));
  const checkedCount = entries.filter(
    (entry) => draft[entry.player_id]?.participated === true
  ).length;

  function update(playerId: string, patch: Partial<DraftEntry>): void {
    setDraft((current) => ({
      ...current,
      [playerId]: {
        participated: patch.participated ?? current[playerId]?.participated ?? false,
        notes: patch.notes ?? current[playerId]?.notes ?? '',
      },
    }));
  }

  function setAllVisible(participated: boolean): void {
    setDraft((current) => {
      const next = { ...current };
      for (const entry of visible) {
        next[entry.player_id] = {
          participated,
          notes: current[entry.player_id]?.notes ?? '',
        };
      }
      return next;
    });
  }

  async function save(): Promise<void> {
    setSaveError(null);
    setIsSaving(true);
    try {
      const saved = await apiClient.updateParticipation(event.event_id, {
        participation: changed.map((entry) => {
          const row = draft[entry.player_id];
          return {
            player_id: entry.player_id,
            participated: row?.participated ?? false,
            notes: (row?.notes ?? '').trim() === '' ? null : (row?.notes ?? '').trim(),
          };
        }),
      });
      participation.apply(saved);
      showToast(
        `Participation saved for ${changed.length} ${changed.length === 1 ? 'player' : 'players'}.`,
        'success'
      );
    } catch (caught) {
      setSaveError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'Participation could not be saved. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <DeskSection
      className="participation"
      title="Roster / Participation"
      description={
        <>
          {checkedCount} of {entries.length} marked as participating ·{' '}
          {participation.page?.unrecorded_count ?? 0} not yet recorded. Program data only — never
          medical, contact or academic notes.
        </>
      }
      actions={
        canWrite ? (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={visible.length === 0}
              onClick={() => setAllVisible(true)}
            >
              Mark all shown
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={visible.length === 0}
              onClick={() => setAllVisible(false)}
            >
              Clear all shown
            </button>
          </>
        ) : undefined
      }
    >
      {!isPersonaAllowed ? (
        <p className="banner" role="status">
          You have read-only access to participation. Team administrators and coaching staff record
          it.
        </p>
      ) : null}
      {isPersonaAllowed && isClosedLock ? (
        <p className="banner banner--warning" role="status">
          This event is closed, so participation is read-only. A team administrator can reopen it.
        </p>
      ) : null}

      <div className="participation__filters">
        <div className="field">
          <label className="field__label" htmlFor="participation-search">
            Search players
          </label>
          <input
            id="participation-search"
            type="search"
            className="field__control"
            value={search}
            placeholder="Name, jersey, position or cohort"
            onChange={(changeEvent) => setSearch(changeEvent.target.value)}
          />
        </div>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={isUnrecordedOnly}
            onChange={(changeEvent) => setIsUnrecordedOnly(changeEvent.target.checked)}
          />
          <span className="checkbox__text">Show only players not yet recorded</span>
        </label>
      </div>

      {participation.error !== null ? (
        <div className="banner banner--error" role="alert">
          <p>{participation.error}</p>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={participation.reload}
          >
            Try again
          </button>
        </div>
      ) : null}
      {saveError !== null ? (
        <div className="banner banner--error" role="alert">
          <p>{saveError}</p>
        </div>
      ) : null}

      {participation.isLoading ? <p role="status">Loading the roster…</p> : null}
      {!participation.isLoading && participation.error === null && entries.length === 0 ? (
        // role="status" wrapper preserved: this only appears after the roster
        // loads, so screen readers should hear it announced.
        <div className="participation__empty" role="status">
          <EmptyState
            isCompact
            title="No players on the roster yet"
            description="Add players on the Roster page and they will appear here."
          />
        </div>
      ) : null}
      {!participation.isLoading && entries.length > 0 && visible.length === 0 ? (
        <EmptyState
          isCompact
          variant="filtered"
          className="participation__empty"
          title="No players match this search"
          description="Try a different name or jersey number."
        />
      ) : null}

      <ul className="participation__list">
        {visible.map((entry) => {
          const row = draft[entry.player_id];
          return (
            <li
              key={entry.player_id}
              className={`participation__row${entry.is_recorded ? '' : ' participation__row--unrecorded'}`}
            >
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={row?.participated ?? false}
                  disabled={!canWrite || isSaving}
                  aria-label={`Participated: ${entry.full_name}`}
                  onChange={(changeEvent) =>
                    update(entry.player_id, { participated: changeEvent.target.checked })
                  }
                />
                <span className="checkbox__text">{describePlayer(entry)}</span>
              </label>
              {canWrite ? (
                <input
                  type="text"
                  className="field__control participation__notes"
                  value={row?.notes ?? ''}
                  maxLength={PARTICIPATION_NOTES_MAX_LENGTH}
                  placeholder="Notes (optional)"
                  aria-label={`Participation notes: ${entry.full_name}`}
                  disabled={isSaving}
                  onChange={(changeEvent) =>
                    update(entry.player_id, { notes: changeEvent.target.value })
                  }
                />
              ) : (
                <span className="participation__notes-readonly">{entry.notes ?? ''}</span>
              )}
              {entry.is_recorded ? null : (
                <span className="badge participation__flag">Not recorded</span>
              )}
            </li>
          );
        })}
      </ul>

      {canWrite ? (
        <div className="btn-row participation__actions">
          <span className="muted" aria-live="polite">
            {changed.length === 0
              ? 'No unsaved changes.'
              : `${changed.length} unsaved ${changed.length === 1 ? 'change' : 'changes'}.`}
          </span>
          <button
            type="button"
            className="btn btn--secondary"
            disabled={changed.length === 0 || isSaving}
            onClick={() => setDraft(toParticipationDraft(entries))}
          >
            Discard changes
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={changed.length === 0 || isSaving}
            onClick={() => void save()}
          >
            {isSaving ? 'Saving…' : 'Save Participation'}
          </button>
        </div>
      ) : null}
    </DeskSection>
  );
}
